import { ChangeDetectionStrategy, Component, Input, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { BehaviorSubject, EMPTY, Observable } from 'rxjs';
import { catchError, finalize } from 'rxjs/operators';
import { Moment } from 'moment/moment';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { TranslateModule } from '@ngx-translate/core';
import { ITask } from '@nimbus-assistant/models/task.model';
import { Modality } from '@nimbus-assistant/models/task-types.model';
import { AssistantTasksService } from '@nimbus-assistant/services/assistant-tasks.service';
import { LoggingService } from '@nimbus-assistant/services/logging.service';
import { getModalityIcon } from '@nimbus-assistant/helpers/task-priority.helpers';
import { ICONS, Icon } from '@nimbus-library/features/shared/resources/icons';
import { IconWeight } from '@nimbus-library/features/shared/directives/icon-weight.enum';
import { TranslationKeys } from '@nimbus-library/features/localization/models/enums/translation-keys.enum';
import { BackgroundTaskFactory } from '@nimbus-library/features/background-task/background-task.factory';
import { SharedUIModule } from '@nimbus-library/features/shared/shared.ui.module';

@UntilDestroy()
@Component({
  standalone: true,
  selector: 'app-task-incoming-overlay',
  templateUrl: './task-incoming-overlay.component.html',
  styleUrls: ['./task-incoming-overlay.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [CommonModule, TranslateModule, SharedUIModule],
})
export class TaskIncomingOverlayComponent implements OnInit {
  @Input({ required: true }) public task: ITask;

  public readonly ICONS = ICONS;
  public readonly IconWeight = IconWeight;
  public readonly TranslationKeys = TranslationKeys;
  public actionInProgress$ = new BehaviorSubject<boolean>(false);
  public currentMoment$: Observable<Moment>;

  public get isAudioTask(): boolean {
    return this.task?.modality === Modality.Audio;
  }

  public get modalityTitle(): TranslationKeys {
    return TranslationKeys.QueueIncoming;
  }

  public get modalityIcon(): Icon {
    return getModalityIcon(this.task);
  }

  public constructor(
    private readonly tasksService: AssistantTasksService,
    private readonly backgroundTaskFactory: BackgroundTaskFactory,
    private readonly loggingService: LoggingService,
  ) {}

  public ngOnInit(): void {
    this.currentMoment$ = this.backgroundTaskFactory.getServerWatches();
  }

  public onAccept(): void {
    this.handleAction(this.tasksService.acceptTask(this.task.unifiedConversationId, this.task.conversationId, this.task.modality));
  }

  public onPark(): void {
    this.handleAction(this.tasksService.parkTask(this.task.unifiedConversationId));
  }

  public onDecline(): void {
    this.handleAction(this.tasksService.declineTask(this.task.unifiedConversationId, this.task.conversationId, this.task.modality));
  }

  private handleAction(action$: Observable<void>): void {
    this.actionInProgress$.next(true);
    action$
      .pipe(
        untilDestroyed(this),
        catchError((err) => {
          this.loggingService.Error(`Incoming overlay action error: ${JSON.stringify(err)}`);
          return EMPTY;
        }),
        finalize(() => this.actionInProgress$.next(false)),
      )
      .subscribe();
  }
}
